import { Camera, ArrowLeft, Check, Info, Pencil, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AddProductByPhoto.css";
import { addBulkProductToWarehouseUrl, addProductToWarehouseUrl, bulkProductCreateUrl, getProductCatelogFromImageUrl } from "../../../../../api/endpoints";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { generateNumericId } from "../../AddStockPage";
import { setWarehouse } from "../../../../../slice/warehouseSlice";
import { getAllData, saveData } from "../../../../../tools/indexdb";
import { setUser } from "../../../../../slice/webStoreUserSlice";
import { uiAlert } from "../../../../../components/alerts/customAlert/uiAlert";

const AddProductByPhoto = ({ changePage, warehouse, setAlertDisplay, socket }) => {
  const navigate = useNavigate();
  const [capturedImage, setCapturedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { store } = useSelector((state) => state.store);
  const user = useSelector((state) => state.webStoreUser.user);
  const currency = user.userCountryCurrencySymbol || "₦";
  const { wareHouseID } = useParams();
  const dispatch = useDispatch();

  const showToast = (title, description) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, title, description }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  };

  const handleImageCapture = async (e) => {
    setIsProcessing(true);
    const file = e.target.files?.[0];
    if (!file) return;
    console.log(file, "captured file");

    const reader = new FileReader();
    reader.onload = (event) => {
      setCapturedImage(event.target?.result);
      //processImage();
    };
    reader.readAsDataURL(file);

    try {
      const form = new FormData();
      form.append("image", file);

      // send to your backend endpoint
      const resp = await fetch(getProductCatelogFromImageUrl + store._id, {
        method: "POST",
        body: form, // content-type => multipart/form-data automatically
        headers: {
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      if (!resp.ok) {
        throw new Error("Failed to process image");
      }

      const { catalog } = await resp.json();
      const processedCatalog = catalog.map((item, index) => ({
        id: index.toString(),
        name: item.productName || "Unnamed Product",
        brand: item.brand || "Unknown Brand",
        quantity: item.quantity || 0,
        costPrice: item.costPrice || 0,
        unitPrice: item.unitPrice || 0,
        category: item.category || "General",
      }));

      setProducts(processedCatalog);

      showToast("Image Processed", `${processedCatalog.length} products extracted from your list.`);
    } catch (error) {
      console.log(error);
      showToast("Error", "There was an error processing your image. Please try again.");
      setProducts([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApprove = async () => {
    //remove this line after

    setIsLoading(true);

    const endpoint = warehouse ? addBulkProductToWarehouseUrl + wareHouseID : bulkProductCreateUrl + store._id;
    // format products for backend
    const formattedProducts = formatProductsForBackend(products, warehouse);
    console.log(formattedProducts, "formatted products", endpoint, "endpoint");

    try {
      let response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        body: JSON.stringify({ products: formattedProducts, employee: null }),
      });
      console.log(response, "response after adding products");

      if (!response.ok) {
        let errorData = await response.json();
        console.log("Error response from server:", errorData);
        throw new Error("Failed to add products");
      }

      if (response.status === 207) {
        let errorData = await response.json();
        console.log("Partial success response from server:", errorData);
        setAlertDisplay({
          showAlert: true,
          type: "warning",
          title: "Partial Success",
          message: `Some products were not added: ${errorData.message || "Unknown error"} only ${errorData.insertedCount || 0} products were added.`,
        });
        return;
      }
      // Success
      //if warehouse update

      if (warehouse) {
        console.log("updating warehouse store");

        let updatedWarehouse = await response.json();
        console.log(updatedWarehouse, "updated warehouse after adding products");

        warehouse && dispatch(setWarehouse(updatedWarehouse));
        uiAlert("Products Added", `${products.length} products have been added to the warehouse inventory.`, "success");
        warehouse && changePage("warehouseStock");
      } else {
        const { storeCollections, user } = await response.json();
        console.log(storeCollections, "updated store collections");

        // update index dB
        let storeData = await getAllData(store.storeName);

        if (storeData) {
          storeData.storeProductCollection = storeCollections.products;

          await saveData(store.storeName, storeData);
          dispatch(setUser(user));
          changePage("stockManagement");
        }
      }

      showToast("Products Added", `${products.length} products have been added to your inventory.`);
    } catch (error) {
      console.log(error);
      setAlertDisplay({
        showAlert: true,
        type: "error",
        title: "Error Adding Products",
        message: "There was an error adding products to your inventory. Please try again.",
      });
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingId(product.id);
    setEditedProduct({ ...product });
  };

  const handleSave = () => {
    if (editedProduct) {
      setProducts(products.map((p) => (p.id === editedProduct.id ? editedProduct : p)));
      setEditingId(null);
      setEditedProduct(null);
      showToast("Product Updated", "Your changes have been saved.");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditedProduct(null);
  };

  const updateField = (field, value) => {
    if (editedProduct) {
      setEditedProduct({ ...editedProduct, [field]: value });
    }
  };

  const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent);
  const allowDigits = (e) => {
    if (e.inputType?.startsWith("insert") && e.data && /[^0-9]/.test(e.data)) {
      e.preventDefault();
    }
  };
  const unWantedInputs = ["+", "-", "e", "E", "."];
  return (
    <div className='photo-capture-page-wrapper'>
      {/* Header */}
      <header className='photo-capture-page-header'>
        <div className='photo-capture-page-header-container'>
          <div className='photo-capture-page-header-content'>
            <button
              onClick={() => {
                warehouse ? changePage("warehouseStock") : changePage("stockManagement");
              }}
              className='photo-capture-page-back-btn'
            >
              <ArrowLeft />
            </button>
            <div>
              <h1 className='photo-capture-page-title'>Add Products by Photo</h1>
              <p className='photo-capture-page-subtitle'>Capture your product list for AI extraction</p>
            </div>
          </div>
        </div>
      </header>

      <main className='photo-capture-page-main'>
        {!capturedImage && !products.length ? (
          <div className='photo-capture-page-upload-section'>
            {/* Example Section */}
            <div className='photo-capture-page-card photo-capture-page-example-card'>
              <div className='photo-capture-page-example-header'>
                <div className='photo-capture-page-example-icon'>
                  <Info />
                </div>
                <div className='photo-capture-page-example-content'>
                  <h2 className='photo-capture-page-example-title'>How to format your product list</h2>
                  <p className='photo-capture-page-example-description'>Write your products clearly with the following minimum information for best results:</p>
                  <div className='photo-capture-page-example-list'>
                    <div className='photo-capture-page-example-item'>
                      <span className='photo-capture-page-example-label'>Product Name:</span>
                      <span className='photo-capture-page-example-value'>Premium Coffee Beans</span>
                    </div>
                    <div className='photo-capture-page-example-item'>
                      <span className='photo-capture-page-example-label'>Brand:</span>
                      <span className='photo-capture-page-example-value'>Mountain Roast</span>
                    </div>
                    <div className='photo-capture-page-example-item'>
                      <span className='photo-capture-page-example-label'>Quantity:</span>
                      <span className='photo-capture-page-example-value'>50 units</span>
                    </div>
                    <div className='photo-capture-page-example-item'>
                      <span className='photo-capture-page-example-label'>Cost Price:</span>
                      <span className='photo-capture-page-example-value'>{currency}12.50</span>
                    </div>
                    <div className='photo-capture-page-example-item'>
                      <span className='photo-capture-page-example-label'>Unit Price:</span>
                      <span className='photo-capture-page-example-value'>{currency}18.99</span>
                    </div>
                    <div className='photo-capture-page-example-divider'></div>
                    <p className='photo-capture-page-example-note'>Repeat for each product...</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Upload Section */}
            <div className='photo-capture-page-card photo-capture-page-upload-card'>
              <div className='photo-capture-page-upload-content'>
                <div className='photo-capture-page-camera-icon-wrapper'>
                  <Camera />
                </div>

                <div className='photo-capture-page-upload-text-wrapper'>
                  <h2 className='photo-capture-page-upload-title'>Capture Your Product List</h2>
                  <p className='photo-capture-page-upload-description'>
                    Take a photo or upload an image of your handwritten or printed product list. We take care of the rest 😉
                  </p>
                </div>

                <div className='photo-capture-page-upload-button-wrapper'>
                  <label htmlFor='camera-capture' style={{ width: "100%" }} className='photo-capture-page-upload-button' role='button'>
                    <Camera />
                    <span>Take Photo</span>
                    <input
                      capture='environment'
                      id='camera-capture'
                      type='file'
                      accept='image/*'
                      onChange={handleImageCapture}
                      className='photo-capture-page-hidden-input'
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className='photo-capture-page-upload-section'>
            {/* Processing State */}
            {isProcessing && (
              <div className='photo-capture-page-card photo-capture-page-processing-card'>
                <div className='photo-capture-page-processing-content'>
                  <div className='photo-capture-page-spinner'></div>
                  <p className='photo-capture-page-processing-title'>Processing your image...</p>
                  <p className='photo-capture-page-processing-description'>AI is extracting product information</p>
                </div>
              </div>
            )}

            {/* Results */}
            {!isProcessing && products.length > 0 && (
              <>
                <div className='photo-capture-page-results-header'>
                  <div>
                    <h2 className='photo-capture-page-results-title'>Extracted Products</h2>
                    <p className='photo-capture-page-results-subtitle'>
                      <Info />
                      Review and approve the products below. You can edit them later.
                    </p>
                  </div>
                  <button disabled={isLoading} onClick={handleApprove} className='photo-capture-page-approve-btn'>
                    <Check />
                    Approve All ({products.length})
                  </button>
                </div>

                <div className='photo-capture-page-products-list'>
                  {products.map((product) => {
                    const isEditing = editingId === product.id;
                    const displayProduct = isEditing && editedProduct ? editedProduct : product;

                    return (
                      <div
                        key={product.id}
                        className={`photo-capture-page-card photo-capture-page-product-card ${isEditing ? "photo-capture-page-product-editing" : ""}`}
                      >
                        <div className='photo-capture-page-product-content'>
                          {/* Mobile Layout */}
                          <div className='photo-capture-page-product-mobile'>
                            <div className='photo-capture-page-product-fields-mobile'>
                              <div className='photo-capture-page-product-field'>
                                <label className='photo-capture-page-product-label'>Product Name</label>
                                {isEditing ? (
                                  <input
                                    type='text'
                                    value={displayProduct.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    className='photo-capture-page-product-input'
                                    autoFocus
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-value photo-capture-page-product-name'>{displayProduct.name}</p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field'>
                                <label className='photo-capture-page-product-label'>Brand</label>
                                {isEditing ? (
                                  <input
                                    type='text'
                                    value={displayProduct.brand}
                                    onChange={(e) => updateField("brand", e.target.value)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-value photo-capture-page-product-brand'>{displayProduct.brand}</p>
                                )}
                              </div>
                            </div>

                            <div className='photo-capture-page-product-grid-mobile'>
                              <div className='photo-capture-page-product-field'>
                                <label className='photo-capture-page-product-label'>Quantity</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    value={displayProduct.quantity}
                                    onChange={(e) => updateField("quantity", parseInt(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-value photo-capture-page-product-quantity'>
                                    <span className='photo-capture-page-product-quantity-value'>{displayProduct.quantity}</span>
                                    <span className='photo-capture-page-product-quantity-unit'>units</span>
                                  </p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field'>
                                <label className='photo-capture-page-product-label'>Cost Price</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    step='0.01'
                                    value={displayProduct.costPrice}
                                    onChange={(e) => updateField("costPrice", parseFloat(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-value photo-capture-page-product-cost'>
                                    {currency}
                                    {displayProduct.costPrice.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className='photo-capture-page-product-price-highlight'>
                              <div className='photo-capture-page-product-field'>
                                <label className='photo-capture-page-product-label'>Unit Price</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    step='0.01'
                                    value={displayProduct.unitPrice}
                                    onChange={(e) => updateField("unitPrice", parseFloat(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-value photo-capture-page-product-price'>
                                    {currency}
                                    {displayProduct.unitPrice.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className='photo-capture-page-product-actions-mobile'>
                              {isEditing ? (
                                <>
                                  <button onClick={handleSave} className='photo-capture-page-btn-full photo-capture-page-btn-save'>
                                    <Check />
                                    Save Changes
                                  </button>
                                  <button onClick={handleCancel} className='photo-capture-page-btn-full photo-capture-page-btn-cancel'>
                                    <X />
                                  </button>
                                </>
                              ) : (
                                <button onClick={() => handleEdit(product)} className='photo-capture-page-btn-full photo-capture-page-btn-edit'>
                                  <Pencil />
                                  Edit Product
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Desktop Layout */}
                          <div className='photo-capture-page-product-desktop'>
                            <div className='photo-capture-page-product-fields-desktop'>
                              <div className='photo-capture-page-product-field-desktop photo-capture-page-product-field-lg'>
                                <label className='photo-capture-page-product-label'>Product</label>
                                {isEditing ? (
                                  <input
                                    type='text'
                                    value={displayProduct.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    className='photo-capture-page-product-input'
                                    autoFocus
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-desktop-value photo-capture-page-product-name' title={displayProduct.name}>
                                    {displayProduct.name}
                                  </p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field-desktop'>
                                <label className='photo-capture-page-product-label'>Brand</label>
                                {isEditing ? (
                                  <input
                                    type='text'
                                    value={displayProduct.brand}
                                    onChange={(e) => updateField("brand", e.target.value)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-desktop-value photo-capture-page-product-brand' title={displayProduct.brand}>
                                    {displayProduct.brand}
                                  </p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field-desktop'>
                                <label className='photo-capture-page-product-label'>Qty</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    value={displayProduct.quantity}
                                    onChange={(e) => updateField("quantity", parseInt(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-desktop-value photo-capture-page-product-quantity'>
                                    {displayProduct.quantity} <span className='photo-capture-page-product-quantity-unit'>units</span>
                                  </p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field-desktop photo-capture-page-product-field-hidden-lg'>
                                <label className='photo-capture-page-product-label'>Cost Price</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    step='0.01'
                                    value={displayProduct.costPrice}
                                    onChange={(e) => updateField("costPrice", parseFloat(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-desktop-value photo-capture-page-product-cost'>
                                    {currency}
                                    {displayProduct.costPrice.toLocaleString()}
                                  </p>
                                )}
                              </div>

                              <div className='photo-capture-page-product-field-desktop'>
                                <label className='photo-capture-page-product-label'>Unit Price</label>
                                {isEditing ? (
                                  <input
                                    key={isMobileDevice ? "mobile" : "desktop"}
                                    pattern={isMobileDevice ? "[0-9]*" : undefined}
                                    type={isMobileDevice ? "text" : "number"}
                                    inputMode='numeric' // shows numeric keyboard on mobile
                                    onBeforeInput={isMobileDevice ? allowDigits : undefined}
                                    onKeyDown={(e) => {
                                      if (unWantedInputs.includes(e.key)) {
                                        e.preventDefault();
                                      }
                                    }}
                                    step='0.01'
                                    value={displayProduct.unitPrice}
                                    onChange={(e) => updateField("unitPrice", parseFloat(e.target.value) || 0)}
                                    className='photo-capture-page-product-input'
                                  />
                                ) : (
                                  <p className='photo-capture-page-product-desktop-value photo-capture-page-product-price'>
                                    {currency}
                                    {displayProduct.unitPrice.toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className='photo-capture-page-product-actions-desktop'>
                              {isEditing ? (
                                <>
                                  <button onClick={handleSave} className='photo-capture-page-btn-icon photo-capture-page-btn-save'>
                                    <Check />
                                  </button>
                                  <button onClick={handleCancel} className='photo-capture-page-btn-icon photo-capture-page-btn-cancel'>
                                    <X />
                                  </button>
                                </>
                              ) : (
                                <button onClick={() => handleEdit(product)} className='photo-capture-page-btn-icon photo-capture-page-btn-edit'>
                                  <Pencil />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* Toast Container */}
      <div className='photo-capture-page-toast-container'>
        {toasts.map((toast) => (
          <div key={toast.id} className='photo-capture-page-toast'>
            <div className='photo-capture-page-toast-title'>{toast.title}</div>
            {toast.description && <div className='photo-capture-page-toast-description'>{toast.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AddProductByPhoto;

export const formatProductsForBackend = (products, warehouse) => {
  const formatted = products.map((product) => ({
    name: product.name,
    brand: product.brand,
    productCategory: product.category || "Others",
    productState: "New",
    unit: "unit",
    quantity: Number(product.quantity),
    stockLimit: 0,
    costPrice: Number(product.costPrice),
    unitPrice: Number(product.unitPrice),
    productDescription: "not provided",
    canBeDelivered: "false",
    deliveryTime: null,
    deliveryFee: null,
    imageUrl: ["https://res.cloudinary.com/ebuka1122/image/upload/v1746897856/static%20images%20storelens/download_bvjych.png"],
    sizeApplicability: false,
    selectedSizes: null,
    colorApplicability: false,
    selectedColors: null,
    createdInStore: warehouse ? false : true,
    createdInWarehouse: warehouse ? true : false,
    scannedID: generateNumericId(12),
  }));
  return formatted;
};
